import React from 'react';
import { Avatar, Tooltip, Typography } from 'antd';
import { RobotOutlined, SoundOutlined } from '@ant-design/icons';
import UserAvatar from './UserAvatar';
import { getInitials, getAvatarColor, getAvatarRingColor } from '../../utils/avatar';

const { Text } = Typography;

const smallAvatarText = {
  color: 'white',
  fontSize: "11px",
  alignItems: "center",
  justifyContent: "center",
  display: "grid",
};

const ViewerAvatarGroup = ({ viewers }) => {
  let avatars = [];
  
  // Handle backwards compatibility - convert strings to objects
  const normalizedViewers = (viewers || []).map((v) => {
    if (typeof v === 'string') {
      return { type: 'user', id: null, name: v, avatar_url: null };
    }
    return v;
  });

  const people = [...normalizedViewers];

  let justAFewPeople = people.length <= 3;
  while (people.length > 0 && (people.length > 3 || justAFewPeople)) {
    let viewer = people.pop();
    if (!viewer) {
      justAFewPeople = false;
      continue;
    }
    
    // Logic from original getViewerAvatars
    if (viewer.name === "GoBe" || viewer.type === 'system') {
      const isBroadcast = viewer.name === 'Broadcast';
      avatars.push(
        <UserAvatar
            key={viewer.id || viewer.name}
            name={viewer.name}
            userId={viewer.id} // Pass userId
            icon={isBroadcast ? <SoundOutlined /> : <RobotOutlined />}
            size={22}
            showTooltip={true}
            style={{ 
                backgroundColor: "#9bb8c7", 
                border: "1px solid #9bb8c7" 
            }}
        />
      );
    } else {
        // UserAvatar handles both src and initials logic
       avatars.push(
        <UserAvatar
            key={viewer.id || viewer.name}
            name={viewer.name}
            userId={viewer.id} // Pass userId for refresh logic
            src={viewer.avatar_url}
            size={viewer.avatar_url ? 22 : 20}
            showTooltip={true}
            // UserAvatar calculates colors automatically if not provided.
            // But original code passed specific styles.
            // UserAvatar uses the same Utils, so it should be fine.
            // HOWEVER, original code for initials had:
            // border: `1px solid ${getAvatarRingColor(viewer.name)}`
            // UserAvatar handles this.
        />
       );
    }
    
    if (people.length <= 3) justAFewPeople = true; // Loop adjust
  }

  // Handle Overflow
  // The original loop logic is a bit weird: 
  // do { pop... } while (people.length > 3 || justAFewPeople)
  // It pops elements from the END. 
  // If length > 3, it pops until 3 are left? No, it pops until loop condition fails.
  // Actually the original logic was:
  // let justAFewPeople = people.length <= 3;
  // do { ... } while (people.length > 3 || justAFewPeople);
  // This effectively renders ALL if <= 3.
  // If > 3, it renders... wait.
  // If people.length is 4. justAFewPeople is false.
  // Loop runs. Pops 1. remaining 3.
  // Condition: 3 > 3 (false) || false -> stops.
  // So it renders 1, leaves 3?
  // Then "if (people.length)" renders +3.
  // So for 4 people: Renders 1 avatar, then +3.
  
  // My loop above needs to replicate this or do it cleaner.
  // Cleaner way:
  // if length <= 3, render all.
  // if length > 3, render length - 3 avatars (which is... 1? no)
  // Usually it shows 3 avatars and then +N.
  // Original logic:
  // people = [A, B, C, D]
  // pop D. render D. remaining [A, B, C].
  // loop check: people.length (3) > 3 is false. justAFewPeople is false. Loop ends.
  // Remaining: A, B, C.
  // Render: D, +3.
  // This seems to show the LAST person, and then +Remaining.
  
  // Refactored logic to be cleaner/same:
  // Re-read original loop carefully.
  /*
  let justAFewPeople = people.length <= 3;
  do {
    let viewer = people.pop(); // Take from end
    ... render viewer ...
  } while (people.length > 3 || justAFewPeople);
  */
  
  // If people = [1, 2]. justAFewPeople = true.
  // 1. Pop 2. Render 2. Rem: [1]. Len 1. 1 > 3 false. OR true. -> Continue.
  // 2. Pop 1. Render 1. Rem: []. Len 0. 0 > 3 false. OR true. -> Continue.
  // 3. Pop undefined. Break.
  
  // If people = [1, 2, 3, 4]. justAFewPeople = false.
  // 1. Pop 4. Render 4. Rem: [1, 2, 3]. Len 3. 3 > 3 false. OR false. -> Stop.
  // Result: Rendered 4. Plus +3 (1,2,3).
  
  // So if > 3, it shows ONE avatar (the last one) and +N-1.
  // That seems... odd, but I will replicate it to be safe.
  
  // Let's implement exactly that logic in the component but cleaner code?
  // Or just copy the loop structure.
  
  // Actually, I'll stick to the original loop structure to ensure visual regression is minimized.
  // Wait, I need to clear `avatars` array before.
  
  return (
    <Avatar.Group size="small" style={{alignItems: 'center'}}>
      {avatars.map((avatar, index) => (
        <React.Fragment key={index}>{avatar}</React.Fragment>
      ))}
       {people.length > 0 && (
        <Tooltip
            color="#70766e"
            title={<Text style={smallAvatarText}>{people.map(p => p.name).join(", ")}</Text>}
        >
            <Avatar style={{ backgroundColor: "#70766e" }}>
            <Text style={smallAvatarText}>+{people.length}</Text>
            </Avatar>
        </Tooltip>
      )}
    </Avatar.Group>
  );
};

export default ViewerAvatarGroup;
